import { useLocation, useNavigate } from "react-router-dom";
import { SafetyOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useOtp } from "../../store/context/otp-context";
import { useDispatch } from "react-redux";
import {
  setUserLogin,
  setAdminLogin,
} from "../../store/redux/actions/userActions";
import { useNotification } from "../../store/context/notification-context";
import React, { useContext, useEffect, useState } from "react";
import { useApiRequests } from "../../utils/api/https/executor";
import { DashboardContext } from "../../store/context/DashboardContext";
import { useTenant } from "../../store/context/tenant-context";
import {
  SelfUserClassificationEnum,
  UserPreferenceEnum,
  OtpVerificationMethodEnum,
} from "../../shared/constants/constants";
import { navigateToAuth } from "../../utils/helper/redirect";
import { usePermissions } from "../../store/context/permission-context";
import { useUserPreference } from "../../store/context/user-preference-context";
import { useTheme } from "../../store/context/theme-context";
import { loadPermissionFlags } from "../../utils/helper/permission-toggle";
import TwoFactorMethodSelector from "../components/TwoFactorMethodSelector";
import LanguageSelector from "../../shared/languages/LanguageSelector";
import TenantSwitcher from "../../components/General/TenantSwitcher";
import { takePostLoginDestination } from "../../utils/helper/entityProcessorRedirect";
import { Button, Input, Typography, Form } from "antd";

const { Title, Text, Link } = Typography;

const OTPPageNew = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const {
    otp,
    resetOTP,
    setOtp,
    setOtpError,
    setIsResendDisabled,
    setSeconds,
  } = useOtp();
  const { triggerNotification } = useNotification();
  const tenant = useTenant();
  const { getUserPreferences, userPreferences } = useUserPreference();
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark";

  const { state } = location;
  const loginContext = state ? JSON.parse(state.loginContext) : null;
  const {
    userId,
    requestId: initialRequestId,
    emailAddress,
    verificationMethodType,
    isAdmin: isAdminFromContext,
  } = loginContext || {};
  const { fetchPolicies } = useContext(DashboardContext);
  const isAdmin = isAdminFromContext ?? location.pathname.startsWith("/admin");

  // State for method selector modal and request tracking
  const [showMethodSelector, setShowMethodSelector] = useState(false);
  const [currentVerificationMethod, setCurrentVerificationMethod] = useState(
    verificationMethodType,
  );
  const [currentRequestId, setCurrentRequestId] = useState(initialRequestId);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
  const inputRefs = [
    React.useRef(null),
    React.useRef(null),
    React.useRef(null),
    React.useRef(null),
    React.useRef(null),
    React.useRef(null),
  ];

  // Determine verification method display text
  const getVerificationMethodText = () => {
    switch (currentVerificationMethod) {
      case OtpVerificationMethodEnum.EMAIL.value:
        return "email";
      case OtpVerificationMethodEnum.SMS.value:
        return "mobile number";
      case OtpVerificationMethodEnum.AUTHENTICATOR_APP.value:
        return "authenticator app";
      default:
        return "email";
    }
  };

  useEffect(() => {
    if (!loginContext) {
      navigateToAuth({ isAdmin: false });
    }
  }, [loginContext, navigate]);

  const { resendOTP, validateOTP } = useApiRequests();
  const { updatePermissions } = usePermissions();
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits

    const newOtpValues = [...otpValues];
    newOtpValues[index] = value;
    setOtpValues(newOtpValues);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs[index + 1].current?.focus();
    }

    // Auto-submit when all 6 digits are filled
    if (index === 5 && value && newOtpValues.every((v) => v)) {
      handleVerifyOTP(newOtpValues.join(""));
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();

    // Check if pasted data is 6 digits
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split("");
      setOtpValues(digits);

      // Focus the last input
      inputRefs[5].current?.focus();

      // Auto-submit
      handleVerifyOTP(pastedData);
    }
  };

  const handleOtpKeyUp = (index, e) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handleVerifyOTP = async (otpCode = null) => {
    try {
      setIsLoading(true);
      const code = otpCode || otpValues.join("");

      if (code.length !== 6) {
        triggerNotification({
          type: "error",
          message: "Invalid OTP",
          description: "Please enter a 6-digit OTP code.",
        });
        return;
      }

      const dataToPost = {
        userId,
        requestId: currentRequestId,
        otp: code,
        otpVerificationMethodType: currentVerificationMethod,
      };

      const response = isAdmin
        ? await validateOTP(dataToPost)
        : await validateOTP(dataToPost);

      if (response?.ok && response?.outData?.isSuccessful) {
        // OTP verification successful - now call login with otpVerificationRequestId
        const loginPayload = {
          userId: emailAddress || userId,
          otpVerificationRequestId: currentRequestId,
        };

        console.log("Calling login with payload:", loginPayload);
        const loginResponse = isAdmin
          ? await dispatch(setAdminLogin(loginPayload))
          : await dispatch(setUserLogin(loginPayload));

        console.log("Login response:", loginResponse);
        const loginResult = loginResponse?.outData;

        if (loginResult?.isSuccessful) {
          console.log("Login successful, setting up user session...");

          // Get user preferences
          await getUserPreferences([
            UserPreferenceEnum.LANGUAGE,
            UserPreferenceEnum.THEME,
          ]);

          setTheme(() => {
            console.log("userPreferences?.theme", userPreferences?.theme);
            return userPreferences?.theme === "dark" ? "dark" : "light";
          });

          fetchPolicies(); // Don't await
          updatePermissions();

          // For admin users, load permission flags
          const isAdminUser =
            loginResult?.data?.userClassification ===
            SelfUserClassificationEnum.BACKOFFICE_ADMINISTRATORS.value;

          if (isAdminUser) {
            try {
              await loadPermissionFlags({ forceRefresh: true });
              console.log("Permission flags loaded for admin user");
            } catch (error) {
              console.error("Error loading permission flags:", error);
              // Continue with navigation even if permission loading fails
            }
          }

          console.log(
            "Navigating to dashboard...",
            isAdminUser ? "/admin-dashboard" : "/dashboard",
          );
          resetOTP();
          navigate(
            takePostLoginDestination(
              isAdminUser ? "/admin-dashboard" : "/dashboard",
            ),
          );
        } else {
          console.error("Login failed:", loginResult);
          triggerNotification({
            type: "error",
            message: "Login Failed",
            description:
              loginResult?.message ||
              "Failed to complete login. Please try again.",
          });
        }
      } else {
        triggerNotification({
          type: "error",
          message: "Verification Failed",
          description:
            response?.outData?.message ||
            "Failed to verify OTP. Please try again.",
        });
      }
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Error",
        description: "An error occurred during verification. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      setIsResending(true);
      const response = await resendOTP({
        userId,
        requestId: currentRequestId,
        verificationMethodType: currentVerificationMethod,
      });

      const result = response?.outData;

      if (result?.isSuccessful) {
        setCountdown(60);
        setCanResend(false);
        setOtpValues(["", "", "", "", "", ""]);
        inputRefs[0].current?.focus();

        triggerNotification({
          type: "success",
          message: "OTP Sent",
          description: `A new OTP has been sent to your ${getVerificationMethodText()}.`,
        });
      } else {
        triggerNotification({
          type: "error",
          message: "Resend Failed",
          description:
            result?.message || "Failed to resend OTP. Please try again.",
        });
      }
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Error",
        description: "An error occurred while resending OTP.",
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleTryDifferentMethod = () => {
    setShowMethodSelector(true);
  };

  const handleMethodSelected = async (newMethod) => {
    try {
      setShowMethodSelector(false);
      setIsLoading(true);

      const response = await resendOTP({
        userId,
        requestId: currentRequestId,
        verificationMethodType: newMethod,
      });

      const result = response?.outData;

      if (result?.isSuccessful) {
        // Update verification method
        setCurrentVerificationMethod(newMethod);

        // Update requestId if provided, otherwise keep current
        if (result?.data?.requestId) {
          setCurrentRequestId(result.data.requestId);
        }

        setCountdown(60);
        setCanResend(false);
        setOtpValues(["", "", "", "", "", ""]);
        inputRefs[0].current?.focus();

        triggerNotification({
          type: "success",
          message: "Verification Method Changed",
          description:
            result?.message ||
            `OTP sent to your ${getVerificationMethodText()}.`,
        });
      } else {
        triggerNotification({
          type: "error",
          message: "Method Change Failed",
          description:
            result?.message || "Failed to change verification method.",
        });
      }
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Error",
        description: "An error occurred while changing verification method.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        background: isDark
          ? `linear-gradient(135deg, #0f0c29 0%, ${tenant.theme.colorPrimary}15 50%, #24243e 100%)`
          : `linear-gradient(135deg, ${tenant.theme.colorPrimary}20 0%, ${tenant.theme.colorPrimary}40 50%, ${tenant.theme.colorPrimary}30 100%)`,
        padding: "20px",
      }}
    >
      {/* Animated Background Elements */}
      <div
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          opacity: "0.1",
          background: `
            radial-gradient(circle at 20% 50%, ${tenant.theme.colorPrimary} 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, ${tenant.theme.colorPrimary} 0%, transparent 50%),
            radial-gradient(circle at 40% 20%, ${tenant.theme.colorPrimary} 0%, transparent 50%)
          `,
        }}
      />

      {/* Decorative Circles */}
      <div
        style={{
          position: "absolute",
          width: "300px",
          height: "300px",
          borderRadius: "50%",
          border: `2px solid ${tenant.theme.colorPrimary}`,
          opacity: "0.2",
          top: "-100px",
          right: "-100px",
        }}
      />
      <div
        style={{
          position: "absolute",
          width: "200px",
          height: "200px",
          borderRadius: "50%",
          border: `2px solid ${tenant.theme.colorPrimary}`,
          opacity: "0.15",
          bottom: "-50px",
          left: "-50px",
        }}
      />

      {/* Top Bar */}
      <div
        style={{
          position: "absolute",
          top: "20px",
          right: "20px",
          zIndex: 10,
          display: "flex",
          gap: "12px",
        }}
      >
        {/* Development Tenant Switcher - Only show on localhost */}
        {process.env.NODE_ENV === "development" &&
          (window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1") && <TenantSwitcher />}
        <LanguageSelector />
      </div>

      {/* Back Button */}
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: "20px",
          left: "20px",
          color: isDark ? "#ffffff" : "#666666",
          zIndex: 10,
          fontWeight: 500,
        }}
      >
        Back
      </Button>

      {/* OTP Card */}
      <div
        style={{
          width: "100%",
          maxWidth: "480px",
          backgroundColor: isDark
            ? "rgba(31, 31, 31, 0.95)"
            : "rgba(255, 255, 255, 0.98)",
          borderRadius: "20px",
          padding: "56px 48px",
          boxShadow: isDark
            ? "0 30px 80px rgba(0, 0, 0, 0.5)"
            : "0 30px 80px rgba(0, 0, 0, 0.2)",
          backdropFilter: "blur(10px)",
          position: "relative",
        }}
      >
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <img
            src={isDark ? tenant.darkLogo : tenant.logo}
            alt={`${tenant.name} Logo`}
            style={{
              height: "100px",
              width: "auto",
              margin: "0 auto",
            }}
          />
        </div>

        {/* Icon */}
        <div
          style={{
            width: "64px",
            height: "64px",
            margin: "0 auto 24px",
            borderRadius: "50%",
            backgroundColor: `${tenant.theme.colorPrimary}20`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <SafetyOutlined
            style={{
              fontSize: "32px",
              color: tenant.theme.colorPrimary,
            }}
          />
        </div>

        {/* Title */}
        <Title
          level={2}
          style={{
            textAlign: "center",
            color: isDark ? "#ffffff" : "#000000",
            fontSize: "28px",
            fontWeight: "600",
            marginBottom: "12px",
          }}
        >
          Verify Your Identity
        </Title>

        {/* Description */}
        <Text
          style={{
            display: "block",
            textAlign: "center",
            color: isDark ? "#a0a0a0" : "#595959",
            fontSize: "15px",
            marginBottom: "32px",
            lineHeight: "1.6",
          }}
        >
          We've sent a 6-digit verification code to your{" "}
          {getVerificationMethodText()}
          {emailAddress &&
            ` (${emailAddress.replace(/(.{2})(.*)(@.*)/, "$1***$3")})`}
        </Text>

        {/* OTP Input */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "center",
            marginBottom: "24px",
          }}
        >
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <Input
              key={index}
              ref={inputRefs[index]}
              value={otpValues[index]}
              onChange={(e) => handleOtpChange(index, e.target.value.slice(-1))}
              onKeyUp={(e) => handleOtpKeyUp(index, e)}
              onPaste={index === 0 ? handleOtpPaste : undefined}
              maxLength={1}
              style={{
                width: "56px",
                height: "56px",
                fontSize: "24px",
                textAlign: "center",
                fontWeight: "600",
                borderRadius: "8px",
                border: `2px solid ${
                  otpValues[index]
                    ? tenant.theme.colorPrimary
                    : isDark
                      ? "rgba(255, 255, 255, 0.2)"
                      : "rgba(0, 0, 0, 0.15)"
                }`,
              }}
            />
          ))}
        </div>

        {/* Verify Button */}
        <Button
          type="primary"
          onClick={() => handleVerifyOTP()}
          loading={isLoading}
          disabled={otpValues.some((v) => !v)}
          style={{
            width: "100%",
            height: "48px",
            borderRadius: "8px",
            fontSize: "16px",
            fontWeight: "600",
            marginBottom: "16px",
            backgroundColor: tenant.theme.colorPrimary,
            borderColor: tenant.theme.colorPrimary,
          }}
        >
          Verify & Continue
        </Button>

        {/* Resend OTP */}
        <div style={{ textAlign: "center", marginBottom: "16px" }}>
          {canResend ? (
            <Link
              onClick={handleResendOTP}
              disabled={isLoading}
              style={{ color: tenant.theme.colorPrimary }}
            >
              Resend Code
            </Link>
          ) : (
            <Text style={{ color: isDark ? "#a0a0a0" : "#595959" }}>
              Resend code in {countdown}s
            </Text>
          )}
        </div>

        {/* Try Different Method */}
        {currentVerificationMethod !==
          OtpVerificationMethodEnum.AUTHENTICATOR_APP.value && (
          <div style={{ textAlign: "center" }}>
            <Link
              onClick={handleTryDifferentMethod}
              style={{ color: tenant.theme.colorPrimary }}
            >
              Try a different verification method
            </Link>
          </div>
        )}

        {/* Back to Login */}
        <div
          style={{
            textAlign: "center",
            marginTop: "24px",
            paddingTop: "24px",
            borderTop: `1px solid ${
              isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)"
            }`,
          }}
        >
          <Text style={{ color: isDark ? "#a0a0a0" : "#595959" }}>
            Remember your password?{" "}
          </Text>
          <Link
            onClick={() => navigateToAuth({ isAdmin })}
            style={{ color: tenant.theme.colorPrimary, fontWeight: 500 }}
          >
            Back to Login
          </Link>
        </div>
      </div>

      {/* Method Selector Modal */}
      <TwoFactorMethodSelector
        visible={showMethodSelector}
        onCancel={() => setShowMethodSelector(false)}
        onSelect={handleMethodSelected}
      />
    </div>
  );
};

export default OTPPageNew;
